import { useToast } from '@chakra-ui/react';
import { useCallback } from 'react';
import { translate } from '../i18n';

/**
 * Returns a function that shows an error toast with the API error message
 * when available, falling back to a generic translated error string.
 *
 * Usage:
 *   const showError = useErrorToast();
 *   try { await mutate(); } catch (err) { showError(err); }
 */
export default function useErrorToast() {
	const toast = useToast();

	return useCallback(
		(error: unknown, fallbackTitle?: string) => {
			const message = extractMessage(error);

			toast({
				title: fallbackTitle ?? translate('common.error'),
				description: message,
				status: 'error',
				position: 'top-right',
				duration: 5000,
				isClosable: true,
				variant: 'subtle',
			});
		},
		[toast],
	);
}

function extractMessage(error: unknown): string | undefined {
	if (!error || typeof error !== 'object') return undefined;

	// Axios error shape: error.response.data.message
	const axiosData = (error as any)?.response?.data;
	if (axiosData) {
		if (typeof axiosData.message === 'string') return axiosData.message;
		if (Array.isArray(axiosData.message)) return axiosData.message.join(', ');
	}

	// Generic Error
	if ('message' in error && typeof (error as any).message === 'string') {
		return (error as any).message;
	}

	return undefined;
}
